import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import DatePickerCalendarWithRange from '@spothero/ui/date-picker-calendar-with-range';
import Button from '@spothero/ui/button';
import Modal from '@spothero/ui/modal';
import ModalContent from '@spothero/ui/modal-content';
import AnimationUtils from '@spothero/utils/animation';
import DOMUtils from '@spothero/utils/dom';
import DateUtils from 'utils/date';
import AirportDateRangePickerInputs from './AirportDateRangePickerInputs';
import WithAirportDateRangePicker from './WithAirportDateRangePicker';

class AirportDateRangePickerMobile extends Component {
    static propTypes = {
        id: PropTypes.string.isRequired,
        title: PropTypes.string,
        showCondensed: PropTypes.bool,
        displayDateFormat: PropTypes.string,
        selectedStartDate: PropTypes.string,
        selectedEndDate: PropTypes.string,
        showCalendar: PropTypes.bool,
        adjustStartOrEnd: PropTypes.string,
        invalidStartDate: PropTypes.bool,
        invalidEndDate: PropTypes.bool,
        onStartInputFocus: PropTypes.func,
        onEndInputFocus: PropTypes.func,
        onDateChange: PropTypes.func,
        onCloseCalendar: PropTypes.func,
        onSubmit: PropTypes.func,
        tetherKey: PropTypes.string,
        weekdays: PropTypes.array,
        disableAfterDate: PropTypes.string,
        isEditing: PropTypes.bool,
    };

    componentDidUpdate(prevProps, prevState) {
        const {showCalendar, selectedStartDate, displayDateFormat} = this.props;
        const {showCalendar: prevShowCalendar} = prevProps;

        // When we open the calendar modal, scroll the user to the selected month
        if (
            showCalendar &&
            showCalendar !== prevShowCalendar &&
            selectedStartDate
        ) {
            const currentMonthStartDate = moment().set({
                date: 1,
                hours: 0,
                minutes: 0,
                seconds: 0,
                milliseconds: 0,
            });
            const selectedDate = moment(selectedStartDate, displayDateFormat);
            // Calculate the difference in an integer number of months
            const monthDifference = moment
                .duration(
                    selectedDate.diff(currentMonthStartDate, 'months'),
                    'months'
                )
                .asMonths();

            if (monthDifference > 0) {
                const calendarTop = DOMUtils.getDocumentOffset(
                    '.DayPicker-Month:first-child'
                ).top;
                const selectedMonthTop = DOMUtils.getDocumentOffset(
                    `.DayPicker-Month:nth-child(${monthDifference + 1})`
                ).top;
                const previousMonthHint = 60;
                // previousMonthHint is the height of the previous week and a little padding
                // We use it to indicate to the user that there is a previous month they can scroll up to
                const selectedMonthOffset =
                    selectedMonthTop - calendarTop - previousMonthHint;

                AnimationUtils.scrollTo(
                    '.AirportDateRangePicker-modal-calendar-container',
                    selectedMonthOffset
                );
            }
        }
    }

    _onDateChange = ({
        selectedStartDate,
        selectedEndDate,
        nextAdjustedStartOrEnd,
    }) => {
        this.props.onDateChange({
            selectedStartDate,
            selectedEndDate,
            nextAdjustedStartOrEnd: 'start',
            nextShowCalendar: true,
        });
    };

    render() {
        const {
            id,
            title,
            showCondensed,
            showCalendar,
            selectedStartDate,
            selectedEndDate,
            adjustStartOrEnd,
            invalidStartDate,
            invalidEndDate,
            onStartInputFocus,
            onEndInputFocus,
            onCloseCalendar,
            onSubmit,
            tetherKey,
            weekdays,
            disableAfterDate,
            isEditing,
        } = this.props;
        const classes = classNames(
            'AirportDateRangePicker',
            `AirportDateRangePicker-${id}`
        );
        const onNonModalSearchButtonClick =
            selectedStartDate && selectedEndDate ? onSubmit : onStartInputFocus;
        const dateTimeFormatted =
            Boolean(selectedStartDate) && Boolean(selectedEndDate)
                ? `${DateUtils.getTransientDateDisplayFormat(
                      selectedStartDate
                  )} -
            ${DateUtils.getTransientDateDisplayFormat(selectedEndDate)}`
                : 'Please select start and end dates';

        return (
            <div className={classes}>
                {showCondensed ? (
                    <div className="AirportDateRangePickerCondensed">
                        <div className="AirportDateRangePickerCondensed-info">
                            <h3>{title}</h3>
                            <p>{dateTimeFormatted}</p>
                        </div>
                        <Button color="secondary" onClick={onStartInputFocus}>
                            Edit
                        </Button>
                    </div>
                ) : (
                    <>
                        <AirportDateRangePickerInputs
                            displayFormat={
                                DateUtils.getTransientDateDisplayFormat
                            }
                            selectedStartDate={selectedStartDate}
                            selectedEndDate={selectedEndDate}
                            adjustStartOrEnd={adjustStartOrEnd}
                            invalidStartDate={invalidStartDate}
                            invalidEndDate={invalidEndDate}
                            onStartInputFocus={onStartInputFocus}
                            onEndInputFocus={onStartInputFocus}
                            key={tetherKey}
                        />
                        <Button
                            className="AirportDateRangePicker-submit"
                            color="primary"
                            onClick={onNonModalSearchButtonClick}
                        >
                            Search
                        </Button>
                    </>
                )}

                {showCalendar && (
                    <Modal
                        className="AirportDateRangePicker-modal"
                        title=""
                        onHidden={onCloseCalendar}
                    >
                        <ModalContent>
                            <div className="AirportDateRangePicker-modal-inputs-container">
                                <AirportDateRangePickerInputs
                                    startPlaceholderText="Select"
                                    endPlaceholderText="Select"
                                    startLabelText="Start Date"
                                    endLabelText="End Date"
                                    displayFormat={
                                        DateUtils.getTransientDateDisplayFormat
                                    }
                                    selectedStartDate={selectedStartDate}
                                    selectedEndDate={selectedEndDate}
                                    adjustStartOrEnd={adjustStartOrEnd}
                                    invalidStartDate={invalidStartDate}
                                    invalidEndDate={invalidEndDate}
                                    onStartInputFocus={onStartInputFocus}
                                    onEndInputFocus={onEndInputFocus}
                                    key={tetherKey}
                                />
                            </div>
                            <div className="AirportDateRangePicker-modal-weekdays-container">
                                <div className="weekdays">
                                    {weekdays.map(day => (
                                        <div className="weekday" key={day}>
                                            {day}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="AirportDateRangePicker-modal-calendar-container">
                                <DatePickerCalendarWithRange
                                    className="AirportDateRangePicker-calendar"
                                    onChange={this._onDateChange}
                                    selectedStartDate={selectedStartDate}
                                    selectedEndDate={selectedEndDate}
                                    numberOfMonths={13}
                                    adjustStartOrEnd={adjustStartOrEnd}
                                    disableAfter={disableAfterDate}
                                    overrideInitialMonthWithSelected={false}
                                />
                            </div>
                            <Button
                                className="AirportDateRangePicker-submit"
                                color="primary"
                                onClick={onSubmit}
                            >
                                {isEditing ? 'Update Search' : 'Search'}
                            </Button>
                        </ModalContent>
                    </Modal>
                )}
            </div>
        );
    }
}

export default WithAirportDateRangePicker(AirportDateRangePickerMobile);
